// SPDX-License-Identifier: AGPL-3.0-only

import { readonly, shallowRef, type DeepReadonly, type ShallowRef } from "vue";

export type AppDialogTone = "default" | "danger";
export type AppDialogKind = "confirm" | "prompt";

export interface ConfirmDialogOptions {
  readonly title: string;
  readonly message: string;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly tone?: AppDialogTone;
  readonly cancelTone?: AppDialogTone;
}

export interface PromptDialogOptions extends ConfirmDialogOptions {
  readonly inputLabel: string;
  readonly initialValue?: string;
  readonly requiredValue?: string;
  readonly validationMessage?: string;
}

export interface AppDialogRequest {
  readonly id: number;
  readonly kind: AppDialogKind;
  readonly title: string;
  readonly message: string;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly tone: AppDialogTone;
  readonly cancelTone: AppDialogTone;
  readonly inputLabel: string | null;
  readonly initialValue: string;
  readonly requiredValue: string | null;
  readonly validationMessage: string | null;
}

type DialogResult = boolean | string | null;

interface QueuedDialog {
  readonly request: AppDialogRequest;
  readonly resolve: (result: DialogResult) => void;
}

const activeDialogReference: ShallowRef<AppDialogRequest | null> = shallowRef(null);
const queue: QueuedDialog[] = [];
let activeResolver: ((result: DialogResult) => void) | null = null;
let nextDialogId: number = 1;

export const activeAppDialog: DeepReadonly<ShallowRef<AppDialogRequest | null>> = readonly(
  activeDialogReference,
);

function activateNextDialog(): void {
  if (activeDialogReference.value !== null) return;
  const nextDialog: QueuedDialog | undefined = queue.shift();
  if (nextDialog === undefined) return;
  activeResolver = nextDialog.resolve;
  activeDialogReference.value = nextDialog.request;
}

function enqueueDialog(request: AppDialogRequest): Promise<DialogResult> {
  return new Promise<DialogResult>((resolve: (result: DialogResult) => void): void => {
    queue.push({ request, resolve });
    activateNextDialog();
  });
}

function baseRequest(
  kind: AppDialogKind,
  options: ConfirmDialogOptions,
): Omit<AppDialogRequest, "inputLabel" | "initialValue" | "requiredValue" | "validationMessage"> {
  const request: Omit<AppDialogRequest, "inputLabel" | "initialValue" | "requiredValue" | "validationMessage"> = {
    id: nextDialogId,
    kind,
    title: options.title,
    message: options.message,
    confirmLabel: options.confirmLabel ?? "Confirm",
    cancelLabel: options.cancelLabel ?? "Cancel",
    tone: options.tone ?? "default",
    cancelTone: options.cancelTone ?? "default",
  };
  nextDialogId += 1;
  return request;
}

export async function confirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
  const result: DialogResult = await enqueueDialog({
    ...baseRequest("confirm", options),
    inputLabel: null,
    initialValue: "",
    requiredValue: null,
    validationMessage: null,
  });
  return result === true;
}

export async function promptDialog(options: PromptDialogOptions): Promise<string | null> {
  const result: DialogResult = await enqueueDialog({
    ...baseRequest("prompt", options),
    inputLabel: options.inputLabel,
    initialValue: options.initialValue ?? "",
    requiredValue: options.requiredValue ?? null,
    validationMessage: options.validationMessage ?? null,
  });
  return typeof result === "string" ? result : null;
}

export function settleAppDialog(result: DialogResult): void {
  const resolve: ((result: DialogResult) => void) | null = activeResolver;
  if (resolve === null || activeDialogReference.value === null) return;
  activeResolver = null;
  activeDialogReference.value = null;
  resolve(result);
  activateNextDialog();
}
